// The report builder (Talk BI spec §7.4, Task 5.1): a data source, its fields, the wells, six visual types and a
// live preview drawn with the report page's own components. The look is the approved comp's "בונה דוחות" tab
// (docs/DESIGN.md, "Report surfaces"); the state and the panel it makes are pure, in bi-builder.mjs.
import {Icon, ReportState, ReportVisual} from './bi-report.jsx';
import {fieldTitle, nameOf} from './bi-report.mjs';
import {qty} from './bi-format.mjs';
import {LIMITS, VISUALS, empty, fieldsOf, accepts, wellOf, place, remove, setFilter, toPanel, autoTitle, previewError, unused, saveText} from './bi-builder.mjs';

const {useState, useEffect, useLayoutEffect, useRef, useMemo} = React;
const DEBOUNCE_MS = 300;      // one preview per pause, never one per keystroke or drop
const VALUES_SHOWN = 60;
const SHOWN_IN_LIST = 400;
const NO_PARAMS = {period: null, choices: {}, compare: false};
const today = () => new Date().toISOString().slice(0, 10);
const VISUAL = {pivot: ['pivot', 'טבלת ציר', 'Pivot'], table: ['table', 'טבלה', 'Table'], bar: ['bars', 'עמודות', 'Bars'],
  stacked: ['stacked', 'מוערמות', 'Stacked'], line: ['line', 'קו', 'Line'], donut: ['donut', 'טבעת', 'Donut']};
const WELLS = {rows: ['שורות', 'Rows', 'עד 2 ממדים', 'Up to 2 dimensions'], cols: ['עמודות', 'Columns', 'ממד אחד', 'One dimension'],
  values: ['ערכים', 'Values', 'עד 4 מדדים', 'Up to 4 measures'], filters: ['מסננים', 'Filters', 'עד 3, ממד וערך', 'Up to 3, a dimension and a value']};
const TO = {rows: ['לשורות', 'to rows'], cols: ['לעמודות', 'to columns'], values: ['לערכים', 'to values'], filters: ['למסננים', 'to filters']};
const iconOf = field => (field.kind === 'measure' ? 'sigma' : field.kind === 'time' || field.grain ? 'calendar' : 'list');
const validReply = r => !!r?.result && Array.isArray(r.result.columns) && Array.isArray(r.result.rows) && !!r.prescription;

// A field in the pane or the full list: drag it to a well, or click it for the add/move/remove menu.
function FieldItem({field, used, code, open, onMenu, onDrag, onDragEnd, lang}) {
  return <button type="button" className="bi-field-item" draggable data-field={field.name} data-used={used || undefined}
    aria-haspopup="menu" aria-expanded={open} onClick={e => onMenu(field, e.currentTarget)}
    onDragStart={e => onDrag(e, field)} onDragEnd={onDragEnd}>
    <Icon name={iconOf(field)}/>
    <span className="bi-field-name" dir="auto">{field.title}{field.ext && <span className="bi-ext"> {field.ext}</span>}</span>
    {code ? <code className="bi-code" dir="ltr">{field.name}</code> : <span/>}
    <Icon name="grip" className="bi-icon bi-grip"/>
    {used && <span className="sr-only">{lang === 'he' ? ' (בשימוש)' : ' (in use)'}</span>}
  </button>;
}

// The click menu (spec §7.4 "without a mouse"): add, move or remove one field. Arrows move, Esc closes and
// returns focus to the field, Tab closes. Fixed to the viewport, so a scrolling pane never clips it.
function FieldMenu({menu, from, onPick, onClose, lang}) {
  const he = lang === 'he';
  const ref = useRef(null);
  const [at, setAt] = useState(null);
  const items = [...Object.keys(LIMITS).map(well => ({key: well, disabled: !accepts(menu.field, well) || from === well,
    label: `${from ? he ? 'העברה' : 'Move' : he ? 'הוספה' : 'Add'} ${TO[well][he ? 0 : 1]}`})),
    ...(from ? [{key: 'remove', label: he ? 'הסרה מההדמיה' : 'Remove from the visual'}] : [])];
  useLayoutEffect(() => {
    const r = menu.anchor.getBoundingClientRect(), box = ref.current.getBoundingClientRect();
    const below = r.bottom + 4 + box.height <= innerHeight - 8;
    const top = below ? r.bottom + 4 : Math.max(8, r.top - 4 - box.height);
    // the menu's inline start meets the field's, clamped into the viewport
    const start = he ? Math.min(r.right, innerWidth - 8) - box.width : r.left;
    setAt({top, left: Math.max(8, Math.min(start, innerWidth - 8 - box.width))});
  }, [menu]);
  // focus once placed: a menu still hidden while it measures cannot take focus
  useEffect(() => {if (at) ref.current.querySelector('[role=menuitem]:enabled')?.focus();}, [at]);
  useEffect(() => {
    const away = e => { if (!ref.current?.contains(e.target) && !menu.anchor.contains(e.target)) onClose(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [menu]);
  const keys = e => {
    const all = [...ref.current.querySelectorAll('[role=menuitem]:enabled')];
    const i = all.indexOf(document.activeElement);
    const go = {ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: all.length - 1}[e.key];
    if (go !== undefined) { e.preventDefault(); all[(go + all.length) % all.length]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); }
    else if (e.key === 'Tab') { e.preventDefault(); onClose(true); }  // focus goes on from the field, not past the builder
  };
  return <div className="bi-menu" role="menu" ref={ref} aria-label={menu.field.title} onKeyDown={keys}
    style={at ? {top: at.top, left: at.left} : {visibility: 'hidden', top: 0, left: 0}}>
    {items.map(item => <button type="button" role="menuitem" key={item.key} tabIndex={-1} disabled={item.disabled}
      onClick={() => onPick(item.key)}>{item.label}</button>)}
  </div>;
}

function Skeleton({lang}) {
  return <div className="bi-skeleton" role="status">
    <span className="sr-only">{lang === 'he' ? 'טוען תצוגה…' : 'Loading the preview…'}</span>
    {[92, 74, 86, 60, 80, 52].map(w => <span key={w} className="bi-skeleton-bar" style={{inlineSize: `${w}%`}} aria-hidden="true"/>)}
  </div>;
}

// board: the open board (null when none is open); boards: the company's boards, for Save when none is open.
// onSave(panel, target) adds the panel (target: a board id, '' for a new board) and rejects with the server's error.
export function ReportBuilder({id, env, lang, board, boards, busy, onSave}) {
  const he = lang === 'he', w = (a, b) => (he ? a : b);
  const [list, setList] = useState({state: 'loading', items: []});
  const [listTry, setListTry] = useState(0);
  const [key, setKey] = useState(() => board?.panels.find(p => p.definition)?.definition.dataset || '');
  const [detail, setDetail] = useState({state: 'idle', ds: null});
  const [detailTry, setDetailTry] = useState(0);
  const [state, setState] = useState(() => empty());
  const [touched, setTouched] = useState(false);
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState(null);
  const [refocus, setRefocus] = useState(null);
  const [over, setOver] = useState(null);
  const [preview, setPreview] = useState({status: 'idle'});
  const [attempt, setAttempt] = useState(0);
  const [tableView, setTableView] = useState(false);
  const [values, setValues] = useState({});
  const [save, setSave] = useState({state: 'idle', text: '', well: null});
  const [target, setTarget] = useState('');
  const [said, say] = useState('');
  const drag = useRef(null);
  const fetched = useRef(new Set());

  useEffect(() => {
    let alive = true;
    setList(l => ({...l, state: 'loading'}));
    PCMAdapter.listBiDatasets(env).then(r => {if (alive) setList({state: 'ready', items: Array.isArray(r?.datasets) ? r.datasets : []});})
      .catch(() => {if (alive) setList({state: 'error', items: []});});
    return () => {alive = false;};
  }, [env, listTry]);
  useEffect(() => {
    if (!key) {setDetail({state: 'idle', ds: null}); return undefined;}
    let alive = true;
    setDetail({state: 'loading', ds: null});
    PCMAdapter.getBiDataset(key, env).then(ds => {if (alive) setDetail({state: 'ready', ds});})
      .catch(() => {if (alive) setDetail({state: 'error', ds: null});});
    return () => {alive = false;};
  }, [key, env, detailTry]);

  const ds = detail.ds;
  const fields = useMemo(() => ds && fieldsOf(ds, lang), [ds, lang]);
  const fieldOf = name => fields && ([...fields.dims, ...fields.measures].find(f => f.name === name) || fields.all.find(f => f.name === name));
  const title = name => fieldTitle(ds, name, lang);
  const auto = ds ? autoTitle(state, ds, lang) : '';
  const named = (touched ? state.title.trim() : '') || auto;
  const saved = ds && toPanel({...state, title: named}, ds);
  // the preview never carries the title: renaming re-runs nothing
  const asked = useMemo(() => (ds ? toPanel({...state, title: ''}, ds) : null), [state, ds]);
  const askedKey = JSON.stringify(asked);

  useEffect(() => {
    if (!asked) {setPreview({status: 'idle'}); return undefined;}
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setPreview(p => ({...p, status: 'loading'}));
      PCMAdapter.previewBiPanel(env, asked, {}, {signal: controller.signal})
        .then(reply => {if (!controller.signal.aborted) setPreview(validReply(reply) ? {status: 'ready', panel: asked, reply} : {status: 'error', panel: asked, error: {}});})
        .catch(error => {if (!controller.signal.aborted) setPreview({status: 'error', panel: asked, error});});
    }, DEBOUNCE_MS);
    // a change aborts the read in flight, so at most one preview is ever waiting
    return () => {clearTimeout(timer); controller.abort();};
  }, [askedKey, env, attempt]);

  // a filter's values: a one-dimension count over the dataset, most rows first, read once per field
  const filterKeys = state.filters.map(f => f.field).join('\u0001');
  useEffect(() => {
    if (!ds) return;
    for (const {field} of state.filters) {
      const k = `${ds.key}:${field}`;
      if (fetched.current.has(k)) continue;
      fetched.current.add(k);
      setValues(m => ({...m, [k]: {state: 'loading', values: []}}));
      PCMAdapter.previewBiPanel(env, {kind: 'table', dataset: ds.key, rows: [field], values: ['__count']}, {})
        .then(r => setValues(m => ({...m, [k]: {state: 'ready', values: [...new Set((r?.result?.rows || []).map(row => row[0])
          .filter(v => v !== null && v !== undefined && v !== '').map(String))].slice(0, VALUES_SHOWN)}})))
        .catch(() => {fetched.current.delete(k); setValues(m => ({...m, [k]: {state: 'error', values: []}}));});
    }
  }, [ds, filterKeys]);

  const apply = (next, words) => {
    if (next === state) {say(words ? w(`לא ניתן: ${words}`, `Not possible: ${words}`) : ''); return;}
    setState(next); setSave(s => ({...s, well: null}));
    if (words) say(words);
  };
  const placeIn = (field, well) => apply(place(state, field, well), `${field.title} ${TO[well][he ? 0 : 1]}`);
  const choose = k => {setKey(k); setState(s => empty(s.visual)); setTouched(false); setMenu(null); setSave({state: 'idle', text: '', well: null});};
  const openMenu = (field, anchor) => setMenu(m => (m?.field.name === field.name && m.anchor === anchor ? null : {field, anchor}));
  const closeMenu = restore => {const anchor = menu?.anchor; setMenu(null); if (restore) anchor?.focus();};
  const pick = action => {
    const {field, anchor} = menu;
    if (action === 'remove') apply(remove(state, field), w(`${field.title} הוסר`, `${field.title} removed`));
    else placeIn(field, action);
    setMenu(null);
    setRefocus({name: field.name, anchor});
  };
  // after a menu pick, focus goes back to the field's own button wherever it now renders (a chip that moved wells
  // is a new element); an effect, so it lands before the next key press is handled
  useEffect(() => {
    if (!refocus) return;
    const root = document.getElementById(id), n = CSS.escape(refocus.name);
    (refocus.anchor.isConnected ? refocus.anchor : root?.querySelector(`[data-chip="${n}"]`) || root?.querySelector(`.bi-field-item[data-field="${n}"]`))?.focus();
    setRefocus(null);
  }, [refocus]);

  const startDrag = (e, field, from = null) => {
    drag.current = {field, from};
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', field.name);
  };
  const endDrag = () => {drag.current = null; setOver(null);};
  const dropProps = well => ({
    onDragOver: e => {
      const d = drag.current;
      if (!d) return;
      const ok = accepts(d.field, well);
      if (over?.well !== well || over.ok !== ok) setOver({well, ok});
      if (ok) {e.preventDefault(); e.dataTransfer.dropEffect = 'move';}
    },
    onDragLeave: e => {if (!e.currentTarget.contains(e.relatedTarget)) setOver(o => (o?.well === well ? null : o));},
    onDrop: e => {e.preventDefault(); const d = drag.current; endDrag(); if (d) placeIn(d.field, well);},
  });

  const current = preview.status !== 'idle' && JSON.stringify(preview.panel) === askedKey;
  const failure = current && preview.status === 'error' ? previewError(preview.error, state, preview.panel, lang) : null;
  const wellError = save.well ? save : failure?.well ? failure : null;
  const left = unused(state);
  const leftWords = {values: w('טבלת ציר מציגה את הערך הראשון בלבד.', 'A pivot shows the first value only.'),
    cols: w('לטבלה אין עמודות; הממד הזה לא מוצג.', 'A table has no columns; this dimension is not shown.'),
    rows: w('בגרף עם עמודה מוצגת השורה הראשונה בלבד.', 'A chart with a column shows the first row only.')};

  const doSave = async () => {
    if (!saved) return;
    setSave({state: 'saving', text: '', well: null});
    try {
      await onSave(saved, target);
      setSave({state: 'idle', text: '', well: null});
    } catch (e) {
      const at = e?.status === 422 ? previewError(e, state, saved, lang) : null;
      setSave({state: 'error', text: saveText(e, lang), well: at?.well || null, reason: at?.reason});
    }
  };

  const chip = (well, item) => {
    const name = well === 'filters' ? item.field : nameOf(item);
    const field = fieldOf(name) || {name, kind: well === 'values' ? 'measure' : 'dim', title: title(name)};
    const shown = field.title || title(name);
    const vals = well === 'filters' ? values[`${ds.key}:${name}`] : null;
    const open = menu?.field.name === name;
    return <div className="bi-wchip" key={name} draggable data-field={name} onDragStart={e => startDrag(e, {...field, title: shown}, well)} onDragEnd={endDrag}>
      <Icon name={iconOf(field)}/>
      <button type="button" className="bi-wchip-name" data-chip={name} dir="auto" aria-haspopup="menu" aria-expanded={open}
        onClick={e => openMenu({...field, title: shown}, e.currentTarget)}>{shown}</button>
      <button type="button" className="bi-wchip-x" aria-label={w(`הסרת ${shown}`, `Remove ${shown}`)} title={w('הסרה', 'Remove')}
        onClick={e => {apply(remove(state, name), w(`${shown} הוסר`, `${shown} removed`)); setRefocus({name, anchor: e.currentTarget});}}><Icon name="x"/></button>
      {well === 'filters' && <select className="input bi-wchip-value" aria-label={w(`ערך עבור ${shown}`, `Value for ${shown}`)} value={item.value ?? ''}
        onChange={e => apply(setFilter(state, name, e.target.value || null))}>
        <option value="">{w('הכול', 'All')}</option>
        {[...new Set([...(item.value !== null ? [item.value] : []), ...(vals?.values || [])])].map(v => <option key={v} value={v}>{v}</option>)}
      </select>}
      {vals?.state === 'loading' && <span className="bi-well-note" role="status">{w('טוען ערכים…', 'Loading values…')}</span>}
      {vals?.state === 'error' && <span className="bi-well-note">{w('לא ניתן לטעון את הערכים.', 'Could not load the values.')}</span>}
    </div>;
  };

  const summary = list.items.find(d => d.key === key);
  const matches = fields ? fields.all.filter(f => !query || `${f.title} ${f.name}`.toLowerCase().includes(query.trim().toLowerCase())) : [];
  const exts = fields ? [...new Set(fields.all.map(f => f.ext).filter(Boolean))] : [];
  const used = name => !!wellOf(state, name);
  const item = (field, code = false) => <FieldItem key={field.name} field={field} used={used(field.name)} code={code} open={menu?.field.name === field.name}
    onMenu={openMenu} onDrag={startDrag} onDragEnd={endDrag} lang={lang}/>;

  let body;
  if (!key) body = <ReportState state="empty" text={w('בחרו מקור נתונים בחלונית השדות כדי להתחיל.', 'Choose a data source in the fields pane to begin.')} lang={lang}/>;
  else if (detail.state === 'error') body = <ReportState state="failed" text={w('לא ניתן לטעון את שדות המקור.', 'Could not load the source’s fields.')} lang={lang}/>;
  else if (!ds) body = <Skeleton lang={lang}/>;
  else if (!asked) {
    body = <ReportState state="empty" lang={lang} text={state.visual === 'table' ? w('גררו מדד לערכים. ממדים בשורות מפרקים אותו.', 'Drag a measure to values. Dimensions in rows break it down.')
      : state.visual === 'pivot' ? w('גררו ממד לשורות ומדד לערכים, או לחצו על שדה ובחרו לאן להוסיף אותו.', 'Drag a dimension to rows and a measure to values, or click a field and choose where it goes.')
        : w('גררו ממד לשורות (הציר) ומדד לערכים, או לחצו על שדה ובחרו לאן להוסיף אותו.', 'Drag a dimension to rows (the axis) and a measure to values, or click a field and choose where it goes.')}/>;
  } else if (failure) body = <>{<ReportState state={failure.state} text={failure.text} note={failure.note} lang={lang}/>}
    {failure.retry && <p className="bi-build-retry"><button type="button" className="btn btn-sm" onClick={() => setAttempt(a => a + 1)}>{w('נסו שוב', 'Try again')}</button></p>}</>;
  else if (preview.status === 'ready' || preview.reply) {
    const drawn = preview.panel.kind === 'pivot' ? {totals: true, heat: true, ...preview.panel} : preview.panel;
    body = <ReportVisual definition={drawn} report={preview.reply} title={named} dataset={ds} params={NO_PARAMS} today={today()}
      table={tableView && preview.panel.kind === 'chart'} height={340} lang={lang}/>;
  } else body = <Skeleton lang={lang}/>;
  const waiting = !!asked && !(current && preview.status !== 'loading');
  const drawnChart = preview.reply && preview.panel?.kind === 'chart' && !failure;
  const notes = current && preview.status === 'ready' ? preview.reply.prescription.notes || [] : [];
  const sub = ds ? [ds.title, w('כל התאריכים', 'All dates'), ...state.filters.filter(f => f.value !== null).map(f => `${title(f.field)}: ${f.value}`)] : [];

  return <div className="bi-builder" id={id}>
    <section className="bi-pane bi-build-fields" aria-labelledby={`${id}-fields`}>
      <div className="bi-pane-head"><Icon name="list"/><h3 id={`${id}-fields`}>{w('שדות', 'Fields')}</h3></div>
      <div className="bi-pane-body">
        <label className="bi-field">{w('מקור נתונים (מסך)', 'Data source (screen)')}
          <select className="input" value={key} disabled={list.state !== 'ready'} onChange={e => choose(e.target.value)}>
            <option value="" disabled>{list.state === 'loading' ? w('טוען מקורות…', 'Loading sources…') : w('בחירת מקור', 'Choose a source')}</option>
            {list.items.map(d => <option key={d.key} value={d.key}>{d.title}</option>)}
            {key && !summary && list.state === 'ready' && <option value={key}>{key}</option>}
          </select></label>
        {summary && <p className="bi-fnote"><bdi className="bi-code" dir="ltr">{summary.ename}</bdi>
          {summary.rows_estimate !== null && summary.rows_estimate !== undefined && <> · {w(`${qty(summary.rows_estimate)} שורות`, `${qty(summary.rows_estimate)} rows`)}</>}</p>}
        {summary?.may_show_more && <p className="bi-fnote" title={summary.may_show_more}>{w('המסך מסנן לפי משתמש; המקור מציג את כל השורות.', 'The screen filters by user; the source shows every row.')}</p>}
        {list.state === 'error' && <p className="bi-fnote" role="alert">{w('לא ניתן לטעון את מקורות הנתונים.', 'Could not load the data sources.')}
          <button type="button" className="bi-link" onClick={() => setListTry(t => t + 1)}>{w('נסו שוב', 'Try again')}</button></p>}
        {detail.state === 'error' && <p className="bi-fnote" role="alert">{w('לא ניתן לטעון את שדות המקור.', 'Could not load the source’s fields.')}
          <button type="button" className="bi-link" onClick={() => setDetailTry(t => t + 1)}>{w('נסו שוב', 'Try again')}</button></p>}
        {detail.state === 'loading' && <p className="bi-fnote" role="status">{w('טוען שדות…', 'Loading fields…')}</p>}
        {fields && <>
          <p className="bi-fgroup" id={`${id}-dims`}>{w('ממדים', 'Dimensions')}</p>
          <div className="bi-field-list" role="group" aria-labelledby={`${id}-dims`}>{fields.dims.map(f => item(f))}</div>
          <p className="bi-fgroup" id={`${id}-measures`}>{w('מדדים', 'Measures')}</p>
          <div className="bi-field-list" role="group" aria-labelledby={`${id}-measures`}>{fields.measures.map(f => item(f))}</div>
          <details className="bi-catalog">
            <summary>{w(`כל ${qty(fields.all.length)} השדות של ${ds.ename}`, `All ${qty(fields.all.length)} fields of ${ds.ename}`)}</summary>
            <p className="bi-fnote">{exts.length ? w(`כולל טבלאות ההרחבה ${exts.join(', ')}.`, `Including the extension tables ${exts.join(', ')}.`)
              : w('כל העמודות של המסך.', 'Every column of the screen.')} {w('עמודת תאריך נכנסת דרך שנה, רבעון וחודש.', 'A date column comes in as year, quarter and month.')}</p>
            <input type="search" className="input" value={query} onChange={e => setQuery(e.target.value)}
              placeholder={w('חיפוש שדה או עמודה', 'Search a field or column')} aria-label={w('חיפוש שדה', 'Search fields')}/>
            <ul className="bi-catalog-list">
              {matches.slice(0, SHOWN_IN_LIST).map(f => <li key={f.name}>{f.kind === 'time'
                ? <span className="bi-field-item" data-fixed="true" title={w('עמודת תאריך: השתמשו בשנה, רבעון או חודש', 'A date column: use year, quarter or month')}>
                  <Icon name="calendar"/><span className="bi-field-name" dir="auto">{f.title}{f.ext && <span className="bi-ext"> {f.ext}</span>}</span>
                  <code className="bi-code" dir="ltr">{f.name}</code><span/></span>
                : item(f, true)}</li>)}
            </ul>
            {!matches.length && <p className="bi-fnote" role="status">{w('אין שדה כזה.', 'No such field.')}</p>}
            {matches.length > SHOWN_IN_LIST && <p className="bi-fnote">{w(`מוצגים ${qty(SHOWN_IN_LIST)} מתוך ${qty(matches.length)}; חפשו כדי לצמצם.`, `Showing ${qty(SHOWN_IN_LIST)} of ${qty(matches.length)}; search to narrow.`)}</p>}
          </details>
        </>}
      </div>
    </section>

    <section className="bi-pane" aria-labelledby={`${id}-visual`}>
      <div className="bi-pane-head"><Icon name="chart"/><h3 id={`${id}-visual`}>{w('הדמיה', 'Visual')}</h3>
        <button type="button" className="bi-link" disabled={!Object.keys(LIMITS).some(k => state[k].length)}
          onClick={() => {apply(empty(state.visual), w('ההדמיה נוקתה', 'The visual was cleared')); setTouched(false);}}><Icon name="reset"/>{w('ניקוי', 'Clear')}</button></div>
      <div className="bi-pane-body">
        <div className="bi-vis-types" role="group" aria-label={w('סוג הדמיה', 'Visual type')}>
          {VISUALS.map(v => <button type="button" key={v} aria-pressed={state.visual === v} onClick={() => apply({...state, visual: v})}>
            <Icon name={VISUAL[v][0]}/>{VISUAL[v][he ? 1 : 2]}</button>)}
        </div>
        {Object.keys(LIMITS).map(well => {
          const head = WELLS[well];
          const items = state[well];
          const err = wellError?.well === well ? wellError.reason : null;
          return <div className="bi-well" key={well}>
            <p className="bi-well-head" id={`${id}-${well}`}><span>{head[he ? 0 : 1]}</span><span className="bi-well-hint">{head[he ? 2 : 3]}</span></p>
            <div className="bi-well-drop" role="group" aria-labelledby={`${id}-${well}`} data-well={well}
              data-drop={over?.well === well ? over.ok ? 'over' : 'refuse' : undefined} data-error={err ? 'true' : undefined} {...dropProps(well)}>
              {items.length ? items.map(i => chip(well, i))
                : <p className="bi-well-empty">{well === 'values' ? w('גררו לכאן מדד, או לחצו על מדד', 'Drag a measure here, or click one')
                  : w('גררו לכאן ממד, או לחצו על ממד', 'Drag a dimension here, or click one')}</p>}
            </div>
            {left[well] && <p className="bi-well-note">{leftWords[well]}</p>}
            {err && <p className="bi-well-error" role="alert">{err}</p>}
          </div>;
        })}
        <div className="bi-well">
          <label className="bi-well-head" htmlFor={`${id}-name`}>{w('שמירה כדוח', 'Save as a report')}</label>
          <div className="bi-save-row">
            <input id={`${id}-name`} className="input" value={touched ? state.title : auto} maxLength={160} disabled={!ds}
              onChange={e => {setTouched(true); setState(s => ({...s, title: e.target.value}));}}/>
            <button type="button" className="btn primary" disabled={!saved || busy || save.state === 'saving'} onClick={doSave}>
              {save.state === 'saving' ? w('שומר…', 'Saving…') : w('שמירה', 'Save')}</button>
          </div>
          {board ? <p className="bi-fnote">{w('יתווסף ללוח', 'Adds to the board')} <bdi>{board.title}</bdi>{w('. בלוח הוא עוקב אחרי מסנני הלוח.', '. There it follows the board’s filters.')}</p>
            : <label className="bi-field">{w('לוח נתונים', 'Dashboard')}
              <select className="input" value={target} onChange={e => setTarget(e.target.value)}>
                <option value="">{w('לוח חדש בשם הדוח', 'A new dashboard named after the report')}</option>
                {boards.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
              </select></label>}
          {save.state === 'error' && <p className="bi-well-error" role="alert">{save.text}</p>}
        </div>
        <details className="bi-spec"><summary>{w('ההגדרה שנשמרת (JSON)', 'The saved definition (JSON)')}</summary>
          <pre className="mono" dir="ltr" tabIndex={0}>{saved ? JSON.stringify(saved, null, 2) : '—'}</pre></details>
      </div>
    </section>

    <div className="bi-build-canvas">
      <div className="bi-build-intro">
        <h2>{w('בונה דוחות', 'Report builder')}</h2>
        <p>{w('בחרו מסך כמקור נתונים וגררו שדות לשורות, לעמודות ולערכים, או לחצו על שדה ובחרו לאן להוסיף אותו. התצוגה מתעדכנת מיד, והשמירה מוסיפה אותה ללוח.',
          'Choose a screen as the data source and drag fields to rows, columns and values, or click a field and choose where it goes. The preview updates at once, and Save adds it to the board.')}</p>
      </div>
      <article className="bi-build-card" aria-labelledby={`${id}-title`} aria-busy={waiting}>
        <header className="bi-build-card-head">
          <div className="bi-visual-title"><h3 id={`${id}-title`} dir="auto">{ds ? named : w('תצוגה מקדימה', 'Preview')}</h3>
            {sub.length > 0 && <p className="bi-visual-sub">{sub.map((part, i) => <React.Fragment key={i}>{i > 0 && ' · '}<bdi>{part}</bdi></React.Fragment>)}</p>}</div>
          {waiting && preview.reply && <span className="bi-build-status" role="status">{w('מעדכן…', 'Updating…')}</span>}
          {drawnChart && <button type="button" className="bi-tool" aria-pressed={tableView} onClick={() => setTableView(t => !t)}
            aria-label={w('תצוגת טבלה', 'Table view')} title={w('תצוגת טבלה', 'Table view')}><Icon name="table"/></button>}
        </header>
        <div className="bi-visual-body">{body}</div>
        {notes.length > 0 && <ul className="bi-doctor" aria-label={w('הערות על אופן התצוגה', 'Notes on how the preview is drawn')}>
          {notes.map(n => <li key={n}><Icon name="doctor"/><span dir="auto">{n}</span></li>)}</ul>}
      </article>
    </div>
    <p className="sr-only" aria-live="polite">{said}</p>
    {menu && <FieldMenu key={menu.field.name} menu={menu} from={wellOf(state, menu.field.name)} onPick={pick} onClose={closeMenu} lang={lang}/>}
  </div>;
}
